import { useMutation, useQuery } from '@tanstack/react-query'
import useCustomToast from '../../hooks/useCustomToast'
import { SubmitHandler, useForm } from 'react-hook-form'
import { Button, Card, Checkbox, Collapsible, Container, Field, Flex, Input, Link, Text } from '@chakra-ui/react'
import { useEffect, useState } from 'react'
import { ApiError, FormsNewsletterCreate, FormsNewsletterService } from '../../client'
import { getAGB, handleError } from '../../utils'
import { useReCaptcha } from '../../hooks/useReCaptcha'
import { useSettings } from "../UI/settings-context"
import { Helmet } from 'react-helmet-async'

const ENVIRONMENT = import.meta.env.VITE_ENVIRONMENT

function NewsletterComponent() {
    const [dsgChecked, setDsgChecked] = useState(false)
    const [open, setOpen] = useState(false)

    const [agbLink, setAgbLink] = useState<string | null>(null)
    const {data: agb} = useQuery({
        ...getAGB()
    })

    useEffect(() => {
        if(agb?.agb) {
            setAgbLink(agb.agb.path)
        }
    }, [agb])

    const policy = useSettings()?.policy

    const siteKey = policy?.captcha_id ?? ""
    const { getToken } = useReCaptcha(siteKey, "contact_form")

    const showToast = useCustomToast()

    const {
        register,
        handleSubmit,
        reset,
        formState: { errors, isSubmitting },
    } = useForm<FormsNewsletterCreate>({
        mode: "onBlur",
        criteriaMode: "all",
        defaultValues: {
            name: "",
            email: "",
        },
    })

    const mutation = useMutation({
        mutationFn: (data: { token: string; body: FormsNewsletterCreate }) =>
        FormsNewsletterService.createFormsNewsletter({ recaptchaToken: data.token, requestBody: data.body }),
        onSuccess: () => {
            showToast("Versendet!", "Newsletter-Anmeldung erhalten, Mail mit Bestätigungslink wurde gesendet", "success")
            setOpen(false)
            reset()
        },
        onError: (err: ApiError) => {
            handleError(err, showToast)
        },
    })

    const onSubmit: SubmitHandler<FormsNewsletterCreate> = async (data) => {
        const isTestEnv = ["local", "CI"].includes(ENVIRONMENT)

        // reCAPTCHA
        let token: string | null = "test-token"

        if (!isTestEnv) {
            token = await getToken()
            if(!token) {
                showToast("Fehler!", "reCAPTCHA konnte nicht ausgeführt werden", "error")
                return
            }
        }

        mutation.mutate({ token, body: data })
    }

    return (
        <>
            <Helmet>
                {policy?.captcha_id && <script src={`https://www.google.com/recaptcha/api.js?render=${policy.captcha_id}`} /> }
            </Helmet>

            <Container my={10}>
                <Card.Root size="lg">
                    <Card.Header>
                        <Card.Title textAlign="center" color="main">Keine Beiträge mehr verpassen</Card.Title>

                        <Card.Description textAlign="center">
                            Jetzt für den Newsletter anmelden, um wöchentlich über die neusten Beiträge informiert zu werden!
                        </Card.Description>
                    </Card.Header>

                    <Collapsible.Root
                        display="flex"
                        flexDir="column"
                        alignItems="center"
                        open={open}
                        onOpenChange={(e) => setOpen(e.open)}
                    >
                        <Collapsible.Trigger
                            bgColor="main"
                            color="light"
                            px={8}
                            py={2}
                            my={10}
                            borderRadius={5}
                            cursor="pointer"
                            border="1px solid"
                            borderColor="main"
                            _hover={{bgColor: 'light', color: "main"}}
                            textTransform="uppercase"
                        >
                            Anmeldung Newsletter
                        </Collapsible.Trigger>

                        <Collapsible.Content>
                            <form onSubmit={handleSubmit(onSubmit)}>
                                <Card.Body>
                                    <Field.Root required invalid={!!errors.name}>
                                        <Field.Label htmlFor="name">
                                            Name
                                            <Field.RequiredIndicator />
                                        </Field.Label>

                                        <Input
                                            id="name"
                                            {...register("name", {
                                                required: "Name ist erforderlich",
                                                maxLength: {
                                                    value: 255,
                                                    message: "Name darf maximal 255 Zeichen lang sein",
                                                },
                                            })}
                                            placeholder="Name"
                                            type="text"
                                        />
                                        {errors.name && (
                                            <Field.ErrorText>{errors.name.message}</Field.ErrorText>
                                        )}
                                    </Field.Root>

                                    <Field.Root required invalid={!!errors.email} mt={6}>
                                        <Field.Label htmlFor="email">
                                            E-Mail
                                            <Field.RequiredIndicator />
                                        </Field.Label>

                                        <Input
                                            id="email"
                                            {...register("email", {
                                                required: "E-Mail ist erforderlich",
                                                maxLength: {
                                                    value: 255,
                                                    message: "E-Mail darf maximal 255 Zeichen lang sein",
                                                },
                                            })}
                                            placeholder="E-Mail"
                                            type="email"
                                        />
                                        {errors.email && (
                                            <Field.ErrorText>{errors.email.message}</Field.ErrorText>
                                        )}
                                    </Field.Root>

                                    <Field.Root mt={6}>
                                        <Checkbox.Root
                                            required
                                            checked={dsgChecked}
                                            onCheckedChange={(e) => setDsgChecked(!!e.checked)}
                                            data-testid="check-dsg"
                                        >
                                            <Checkbox.HiddenInput />
                                            <Checkbox.Control />
                                            <Checkbox.Label>Ich habe die <Link color="main" href="/datenschutz" target="_blank">Datenschutzerklärung</Link> und <Link color="main" href={agbLink ?? "/"} target="_blank">AGB</Link> gelesen und akzeptiert</Checkbox.Label>
                                        </Checkbox.Root>
                                    </Field.Root>

                                    <Flex justifyContent="center" mt={6}>
                                        <Text color="dim" fontSize="sm">Diese Website ist durch reCAPTCHA geschützt.</Text>
                                    </Flex>
                                </Card.Body>

                                <Card.Footer justifyContent="center">
                                    <Button
                                        mt={6}
                                        variant="primary"
                                        textTransform="uppercase"
                                        type="submit"
                                        loading={isSubmitting}
                                        disabled={!dsgChecked}
                                    >
                                        Anmelden
                                    </Button>
                                </Card.Footer>
                            </form>
                        </Collapsible.Content>
                    </Collapsible.Root>
                </Card.Root>
            </Container>
        </>
    )
}

export default NewsletterComponent
